import { useQuery } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';

// #10c 조합 옵션 reload — be-catalog 45088f4 계약.
// GET /api/admin/v1/products/{id}/option-groups → 옵션그룹 + 조합 variant 목록.
// 생성 OpenAPI 타입 미반영 → raw fetch + 수동 envelope 파싱.
// 수정(update)은 BE 1차 미반영이라 편집기는 등록에서만 활성, 편집 화면은 조회 표시 전용.

export interface OptionGroupChoiceView {
  choiceId: string;
  label: string;
  additionalAmount: number | null;
  position: number;
}

export interface OptionGroupView {
  groupId: string;
  name: string;
  required: boolean;
  position: number;
  choices: ReadonlyArray<OptionGroupChoiceView>;
}

export interface VariantCombinationView {
  variantId: string;
  label: string;
  additionalAmount: number | null;
  /** 이 조합을 구성하는 (그룹, 선택지) 쌍. */
  choices: ReadonlyArray<{ groupId: string; choiceId: string }>;
}

export interface ProductOptionGroups {
  optionGroups: ReadonlyArray<OptionGroupView>;
  variantCombinations: ReadonlyArray<VariantCombinationView>;
}

export const productOptionGroupsQueryKey = (productId: string) =>
  ['admin', 'product-option-groups', productId] as const;

// 와이어 표현(BE 정본 ProductOptionGroupView.java: Choice.choiceId, Link{groupId, choiceId}).
// 방어적 폴백: 혹시 groupChoiceId 로 오는 경우도 수용(계약 문구가 한때 엇갈렸던 이력).
export interface RawChoice {
  choiceId?: string;
  groupChoiceId?: string;
  label?: string;
  additionalAmount?: number | null;
  position?: number;
}
export interface RawGroup {
  groupId?: string;
  name?: string;
  required?: boolean;
  position?: number;
  choices?: RawChoice[];
}
export interface RawLink {
  groupId?: string;
  choiceId?: string;
  groupChoiceId?: string;
}
export interface RawCombination {
  variantId?: string;
  label?: string;
  additionalAmount?: number | null;
  choices?: RawLink[];
}
interface OptionGroupsEnvelope {
  success?: boolean;
  // ★BE 정본 record 필드명은 `groups`(ProductOptionGroupView.java:13) — 종전 파서가 읽던
  // optionGroups 는 어떤 BE 버전에도 없던 키라 시드가 항상 빈 결과였다(조회전용 시절엔
  // 에러폴백 표시와 구분이 안 가 미발견). groups 정본 + optionGroups 방어 폴백.
  data?: {
    groups?: RawGroup[];
    optionGroups?: RawGroup[];
    variantCombinations?: RawCombination[];
  };
  error?: { code?: string; message?: string } | null;
}

// choiceId 정본, groupChoiceId 방어 폴백.
const resolveChoiceId = (c: { choiceId?: string; groupChoiceId?: string }): string =>
  c.choiceId ?? c.groupChoiceId ?? '';

export const normalizeOptionGroup = (g: RawGroup, gi: number): OptionGroupView => ({
  groupId: g.groupId ?? '',
  name: g.name ?? '',
  required: g.required ?? false,
  position: g.position ?? gi,
  choices: (g.choices ?? []).map((c, ci) => ({
    choiceId: resolveChoiceId(c),
    label: c.label ?? '',
    additionalAmount: c.additionalAmount ?? null,
    position: c.position ?? ci,
  })),
});

export const normalizeVariantCombination = (v: RawCombination): VariantCombinationView => ({
  variantId: v.variantId ?? '',
  label: v.label ?? '',
  additionalAmount: v.additionalAmount ?? null,
  choices: (v.choices ?? []).map((l) => ({ groupId: l.groupId ?? '', choiceId: resolveChoiceId(l) })),
});

/**
 * 편집 모드에서만 조회(enabled=Boolean(productId)). 조합 옵션이 없는 상품은 빈 배열을 반환한다.
 * BE 정본 필드명 choiceId(ProductOptionGroupView.java)로 파싱, groupChoiceId 방어 폴백.
 */
export const useProductOptionGroupsQuery = (productId: string | null | undefined) =>
  useQuery<ProductOptionGroups>({
    queryKey: productOptionGroupsQueryKey(productId ?? ''),
    enabled: Boolean(productId),
    queryFn: async () => {
      // ★경로 /admin/v1 — catalog alias 핫픽스 정합. 종전 /api/admin/v1 은 gateway catalog
      // route(/admin/v1/products/** 무rewrite)와 컨트롤러 경로가 어긋나 양 경로 404 였다(라이브 검증).
      const res = await adminFetch(`/admin/v1/products/${productId as string}/option-groups`);
      if (!res.ok) {
        throw new Error(`option-groups failed: ${res.status}`);
      }
      const env = (await res.json()) as OptionGroupsEnvelope;
      if (env.success === false) {
        throw new Error(env.error?.message ?? env.error?.code ?? 'option-groups error');
      }
      return {
        optionGroups: (env.data?.groups ?? env.data?.optionGroups ?? []).map(normalizeOptionGroup),
        variantCombinations: (env.data?.variantCombinations ?? []).map(normalizeVariantCombination),
      };
    },
  });
